import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from 'src/axiosInstance';
import { BallTriangle } from 'react-loader-spinner';
import { Avatar } from '@mui/material';

// Helper to extract number from MongoDB Decimal128 or fallback to number
const getNumber = (val) => {
  if (typeof val === 'object' && val !== null && '$numberDecimal' in val) {
    return Number(val.$numberDecimal);
  }
  return Number(val) || 0;
};

function MobileOrderConfirm() {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axiosInstance.get(`/shop/order/get-customer-order/${order_id}`);
        setOrder(res.data.data[0]);
      } catch (err) {
        setError('Failed to fetch order details.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [order_id]);

  const handleConfirmOrder = async () => {
    if (!order) return;
    setConfirming(true);
    setError('');
    try {
      await axiosInstance.put(`/shop/order/mark-user-order/${order_id}`, {
        order_number: order.order_number,
      });
      setOrder({ ...order, order_status: 'completed' });
    } catch (err) {
      setError('Failed to confirm order.');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <BallTriangle height={50} width={55} radius={5} color="#af3585" ariaLabel="ball-triangle-loading" visible />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: 40 }}>{error}</div>;
  }

  if (!order) return null;

  const isCompleted = order.status === 'completed';
  const statusColor = isCompleted ? '#12B76A' : '#F79009';
  const statusBg = isCompleted ? '#E8F5E9' : '#FFF3E0';

  return (
    <div style={{ background: '#f7f7f7', minHeight: '100vh', fontFamily: 'Montserrat, sans-serif', maxWidth: 430, margin: '0 auto', padding: 0 }}>
      <div style={{ padding: '18px 0 0 0', background: '#fff', borderBottom: '1px solid #eee' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', fontSize: 22, marginLeft: 12, cursor: 'pointer' }}>&larr;</button>
        <span style={{ fontWeight: 700, fontSize: 23, color: '#222', marginLeft: 18 }}>Confirm Orders</span>
      </div>
      <div style={{ padding: 18 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.01)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar src={order.customer_profile_image || '/assets/images/avatars/avatar_1.jpg'} alt={order.customer_name} style={{ width: 56, height: 56, marginRight: 16 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700, fontSize: 20 }}>{order.admin}</div>
                <div style={{ background: statusBg, color: statusColor, fontWeight: 600, fontSize: 15, borderRadius: 7, padding: '4px 12px', minWidth: 70, textAlign: 'center', marginTop: 6, marginLeft: 0, alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>
                  {order.status
                    ? order.status.charAt(0).toUpperCase() + order.status.slice(1)
                    : "Unknown"}
                </div>
              </div>
              <div style={{ marginTop: 4 }}>
                <div style={{ color: '#757575', fontSize: 14 }}>{order.email}</div>
                <div style={{ color: '#757575', fontSize: 14, marginTop: 2 }}>{order.phone_number}</div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 24 }}>
          {(order.items || []).map((item, idx) => (
            <div key={idx} style={{ background: '#fff', borderRadius: 12, padding: 18, marginBottom: 16, display: 'flex', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.01)' }}>
              <img src={item.product_info?.image || item.image || '/assets/images/products/product_default.jpg'} alt={item.name || item.product_info?.name} style={{ width: 60, height: 60, borderRadius: 8, marginRight: 18, objectFit: 'cover' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{item.name || item.product_info?.name || item.product_name}</div>
                <div style={{ color: '#757575', fontSize: 13 }}>Qty: {item.quantity || item.qty}</div>
                <div style={{ color: '#757575', fontSize: 13 }}>₦{Number(item.price || item.unit_price).toLocaleString()}/Unit</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#222', minWidth: 80, textAlign: 'right' }}>
                ₦{Number(item.total || item.amount || (item.price || item.unit_price) * (item.quantity || item.qty)).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, marginTop: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.01)' }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>Summary</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Total Order:</span>
            <span>₦{getNumber(order.total_order).toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Total Discount:</span>
            <span>₦{getNumber(order.discount || 0).toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Customer Total:</span>
            <span>₦{getNumber(order.customer_to_pay || order.total_order).toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 20, marginTop: 10 }}>
            <span>Sub-Total:</span>
            <span>₦{getNumber(order.customer_to_pay || order.total_order).toLocaleString()}</span>
          </div>
        </div>
        {!isCompleted && (
          <button onClick={handleConfirmOrder} disabled={confirming} style={{ marginTop: 32, width: '100%', background: '#af3585', color: '#fff', border: 'none', borderRadius: 8, padding: '16px 0', fontWeight: 700, fontSize: 18, cursor: 'pointer' }}>
            {confirming ? 'Completing...' : 'Complete Order'}
          </button>
        )}
      </div>
    </div>
  );
}

export default MobileOrderConfirm; 